import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ActiveTipSequenceType, SidebarSectionType } from 'data/types';
import { refreshActiveTipSequence, setActiveTipSequence } from 'features/ui';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { buildStepTipSequence, getTipDismissKey } from 'utils/tips';

type UseStepTipSequenceArgs = {
  sections: SidebarSectionType[];
  currentSectionId: string;
  currentStepComplete: boolean;
  suppressed?: boolean;
};

type UseStepTipSequenceResult = {
  currentStepTipSequence: ActiveTipSequenceType | null;
  reopenTipSequence: () => void;
};

export function useStepTipSequence({
  sections,
  currentSectionId,
  currentStepComplete,
  suppressed = false
}: UseStepTipSequenceArgs): UseStepTipSequenceResult {
  const dispatch = useAppDispatch();
  const activeTipSequence = useAppSelector(
    (state) => state.ui.activeTipSequence
  );
  const dismissedTips = useAppSelector((state) => state.ui.dismissedTips);
  const tipsSkipped = useAppSelector((state) => state.ui.tipsSkipped);

  const currentStepTipSequence = useMemo(
    () => buildStepTipSequence(sections, currentSectionId),
    [sections, currentSectionId]
  );
  const currentSectionConfig = useMemo(
    () => sections.find((section) => section.id === currentSectionId),
    [sections, currentSectionId]
  );
  const currentSectionDismissKey = currentSectionConfig
    ? getTipDismissKey(currentSectionConfig)
    : undefined;

  const currentStepTipSequenceRef = useRef(currentStepTipSequence);
  currentStepTipSequenceRef.current = currentStepTipSequence;
  const stepCompleteRef = useRef(currentStepComplete);
  stepCompleteRef.current = currentStepComplete;
  const activeTipSequenceRef = useRef(activeTipSequence);
  activeTipSequenceRef.current = activeTipSequence;

  useEffect(() => {
    if (suppressed) {
      return;
    }
    const tipSequence = currentStepTipSequenceRef.current;
    const tipDismissed =
      !!tipSequence?.dismissKey &&
      dismissedTips.includes(tipSequence.dismissKey);
    const stepBlocksTip =
      stepCompleteRef.current && !tipSequence?.showWhenComplete;
    const shouldShow =
      !!tipSequence && !stepBlocksTip && !tipsSkipped && !tipDismissed;

    if (shouldShow) {
      dispatch(setActiveTipSequence(tipSequence));
      return;
    }

    const manuallyReopened =
      !!tipSequence &&
      !!activeTipSequenceRef.current &&
      activeTipSequenceRef.current.dismissKey === tipSequence.dismissKey;
    if (!manuallyReopened) {
      dispatch(setActiveTipSequence(null));
    }
  }, [currentSectionId, dismissedTips, tipsSkipped, suppressed, dispatch]);

  useEffect(() => {
    if (!currentStepComplete) {
      return;
    }
    const tipSequence = currentStepTipSequenceRef.current;
    const active = activeTipSequenceRef.current;
    if (
      tipSequence &&
      !tipSequence.showWhenComplete &&
      active &&
      active.dismissKey === tipSequence.dismissKey
    ) {
      dispatch(setActiveTipSequence(null));
    }
  }, [currentStepComplete, dispatch]);

  useEffect(() => {
    if (suppressed) {
      return;
    }
    const tipSequence = currentStepTipSequence;

    if (!tipSequence) {
      if (
        activeTipSequence &&
        !!activeTipSequence.dismissKey &&
        activeTipSequence.dismissKey === currentSectionDismissKey
      ) {
        dispatch(setActiveTipSequence(null));
      }
      return;
    }

    if (activeTipSequence) {
      if (
        activeTipSequence.dismissKey === tipSequence.dismissKey &&
        JSON.stringify(activeTipSequence.tips) !==
          JSON.stringify(tipSequence.tips)
      ) {
        dispatch(refreshActiveTipSequence(tipSequence));
      }
      return;
    }

    const tipDismissed =
      !!tipSequence.dismissKey &&
      dismissedTips.includes(tipSequence.dismissKey);
    const stepBlocksTip =
      stepCompleteRef.current && !tipSequence.showWhenComplete;
    if (!stepBlocksTip && !tipsSkipped && !tipDismissed) {
      dispatch(setActiveTipSequence(tipSequence));
    }
  }, [
    currentStepTipSequence,
    currentSectionDismissKey,
    activeTipSequence,
    dismissedTips,
    tipsSkipped,
    suppressed,
    dispatch
  ]);

  useEffect(() => {
    return () => {
      const active = activeTipSequenceRef.current;
      const ownDismissKey = currentStepTipSequenceRef.current?.dismissKey;
      if (active && active.dismissKey === ownDismissKey) {
        dispatch(setActiveTipSequence(null));
      }
    };
  }, [dispatch]);

  const reopenTipSequence = useCallback(() => {
    if (currentStepTipSequenceRef.current) {
      dispatch(setActiveTipSequence(currentStepTipSequenceRef.current));
    }
  }, [dispatch]);

  return { currentStepTipSequence, reopenTipSequence };
}
